/**
 * Encrypted local caches for the Solana app: notes (display until the next
 * authoritative sync) and history. Keyed per network, pool and wallet, and
 * sealed under a key derived from the account's spending key, like the
 * settlement journal.
 */
import type { StoredJoinSplitNote } from "../joinsplit/types";
import type { SolanaHistoryEntry } from "./types";

const PREFIX = "stealthos_solana_app_v1";
const BIGINT_PREFIX = "__stealthos_bigint:";
const writes = new Map<string, Promise<void>>();

function replacer(_key: string, value: unknown): unknown {
  return typeof value === "bigint" ? `${BIGINT_PREFIX}${value}` : value;
}

function reviver(_key: string, value: unknown): unknown {
  if (typeof value === "string" && value.startsWith(BIGINT_PREFIX)) {
    return BigInt(value.slice(BIGINT_PREFIX.length));
  }
  return value;
}

function encodeBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export interface SolanaCacheScope {
  networkKey: string;
  programId: string;
  address: string;
  spendingKey: Uint8Array;
}

function storageKey(scope: SolanaCacheScope, kind: string): string {
  return `${PREFIX}:${kind}:${scope.networkKey}:${scope.programId}:${scope.address}`;
}

async function cacheKey(
  scope: SolanaCacheScope,
  kind: string,
): Promise<CryptoKey | null> {
  if (
    typeof window === "undefined" ||
    !window.localStorage ||
    !globalThis.crypto?.subtle
  ) {
    return null;
  }
  const label = new TextEncoder().encode(`${storageKey(scope, kind)}:`);
  const material = new Uint8Array(label.length + scope.spendingKey.length);
  material.set(label, 0);
  material.set(scope.spendingKey, label.length);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", material);
  return globalThis.crypto.subtle.importKey(
    "raw",
    digest,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

async function load<T>(
  scope: SolanaCacheScope,
  kind: string,
): Promise<T | null> {
  await writes.get(storageKey(scope, kind))?.catch(() => {});
  const key = await cacheKey(scope, kind);
  if (!key) return null;
  const raw = window.localStorage.getItem(storageKey(scope, kind));
  if (!raw) return null;
  try {
    return (await openCachedValue(raw, key)) as T;
  } catch {
    // Another private key may own this cache. A failed read is never evidence
    // that its notes can be discarded; keep the exact ciphertext in place.
    return null;
  }
}

async function openCachedValue(raw: string, key: CryptoKey): Promise<unknown> {
  const payload = JSON.parse(raw) as { iv: string; ciphertext: string };
  const plaintext = await globalThis.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: decodeBase64(payload.iv) },
    key,
    decodeBase64(payload.ciphertext),
  );
  return JSON.parse(new TextDecoder().decode(plaintext), reviver);
}

async function preserveUnreadableCache(
  name: string,
  raw: string,
): Promise<void> {
  const digest = new Uint8Array(
    await globalThis.crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(raw),
    ),
  );
  const digestHex = Array.from(digest, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  const preservedName = `${name}:preserved:${digestHex}`;
  const existing = window.localStorage.getItem(preservedName);
  if (existing !== null && existing !== raw)
    throw new Error("Encrypted Solana cache preservation conflict");
  if (existing === null) window.localStorage.setItem(preservedName, raw);
  if (window.localStorage.getItem(preservedName) !== raw)
    throw new Error("Encrypted Solana cache preservation did not persist");
}

function save(
  scope: SolanaCacheScope,
  kind: string,
  value: unknown,
): Promise<void> {
  const savedScope = {
    ...scope,
    spendingKey: new Uint8Array(scope.spendingKey),
  };
  const name = storageKey(savedScope, kind);
  // Snapshot before yielding, and serialize encryption as well as setItem.
  // Otherwise an older encryption can finish last and erase newer notes.
  const plaintext = new TextEncoder().encode(JSON.stringify(value, replacer));
  const pending = (writes.get(name) ?? Promise.resolve())
    .catch(() => {})
    .then(async () => {
      const previous = window.localStorage.getItem(name);
      const key = await cacheKey(savedScope, kind);
      if (!key)
        throw new Error("Encrypted Solana cache storage is unavailable");
      if (previous !== null) {
        let readable = false;
        try {
          await openCachedValue(previous, key);
          readable = true;
        } catch {
          // Retain notes from a different restored key, including unreadable
          // records, before replacing this wallet's active cache slot.
        }
        if (!readable) await preserveUnreadableCache(name, previous);
      }
      const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
      const ciphertext = await globalThis.crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        plaintext,
      );
      if (window.localStorage.getItem(name) !== previous)
        throw new Error("Encrypted Solana cache changed during save");
      window.localStorage.setItem(
        name,
        JSON.stringify({
          iv: encodeBase64(iv),
          ciphertext: encodeBase64(new Uint8Array(ciphertext)),
          updatedAt: Date.now(),
        }),
      );
    });
  writes.set(name, pending);
  void pending
    .finally(() => {
      if (writes.get(name) === pending) writes.delete(name);
    })
    .catch(() => {});
  return pending;
}

export async function loadSolanaNotesCache(
  scope: SolanaCacheScope,
): Promise<StoredJoinSplitNote[]> {
  const notes = await load<StoredJoinSplitNote[]>(scope, "notes");
  return Array.isArray(notes) ? notes : [];
}

export function saveSolanaNotesCache(
  scope: SolanaCacheScope,
  notes: StoredJoinSplitNote[],
): Promise<void> {
  return save(scope, "notes", notes);
}

export async function loadSolanaHistoryCache(
  scope: SolanaCacheScope,
): Promise<SolanaHistoryEntry[]> {
  const entries = await load<SolanaHistoryEntry[]>(scope, "history");
  return Array.isArray(entries) ? entries : [];
}

export function saveSolanaHistoryCache(
  scope: SolanaCacheScope,
  entries: SolanaHistoryEntry[],
): Promise<void> {
  return save(scope, "history", entries);
}
